import * as types from "../constants/invoiceConstants";

// ============= UTILITY FUNCTIONS =============

/**
 * Creates a standard async reducer state handler
 */
const createAsyncReducer = (initialState, requestType, successType, failType, resetType = null) => {
  return (state = initialState, action) => {
    switch (action.type) {
      case requestType:
        return { ...state, loading: true, error: null };
      case successType:
        return { loading: false, ...action.payload, error: null };
      case failType:
        return { ...state, loading: false, error: action.payload };
      case resetType:
        return initialState;
      default:
        return state;
    }
  };
};

/**
 * Creates a list reducer with standard structure
 */
const createListReducer = (dataKey, requestType, successType, failType, resetType = null) => {
  return (state = { [dataKey]: [] }, action) => {
    switch (action.type) {
      case requestType:
        return { ...state, loading: true, error: null };
      case successType:
        return { loading: false, [dataKey]: action.payload, error: null };
      case failType:
        return { ...state, loading: false, error: action.payload };
      case resetType:
        return { [dataKey]: [] };
      default:
        return state;
    }
  };
};

/**
 * Creates a details reducer with standard structure
 */
const createDetailsReducer = (dataKey, requestType, successType, failType, resetType) => {
  return (state = { [dataKey]: {} }, action) => {
    switch (action.type) {
      case requestType:
        return { ...state, loading: true, error: null };
      case successType:
        return { loading: false, [dataKey]: action.payload, error: null };
      case failType:
        return { ...state, loading: false, error: action.payload };
      case resetType:
        return { [dataKey]: {} };
      default:
        return state;
    }
  };
};

/**
 * Creates a CRUD operation reducer (create/update/delete)
 */
const createCrudReducer = (requestType, successType, failType, resetType, dataKey = null) => {
  return (state = {}, action) => {
    switch (action.type) {
      case requestType:
        return { loading: true, error: null };
      case successType:
        return {
          loading: false,
          success: true,
          ...(dataKey ? { [dataKey]: action.payload } : {}),
          error: null,
        };
      case failType:
        return { loading: false, error: action.payload, success: false };
      case resetType:
        return {};
      default:
        return state;
    }
  };
};

// ============= PRODUCT REDUCERS =============

/**
 * Main product list reducer
 */
export const invoiceProductListReducer = createListReducer(
  "products",
  types.INVOICE_PRODUCT_LIST_REQUEST,
  types.INVOICE_PRODUCT_LIST_SUCCESS,
  types.INVOICE_PRODUCT_LIST_FAIL
);

/**
 * Duplicate product list for filtering
 */
export const invoiceProductListReducerDupli = createListReducer(
  "productsDupli",
  types.INVOICE_DUPLI_PRODUCT_LIST_REQUEST,
  types.INVOICE_DUPLI_PRODUCT_LIST_SUCCESS,
  types.INVOICE_DUPLI_PRODUCT_LIST_FAIL
);

/**
 * Pack types list reducer
 */
export const invoicePackTypeListReducer = createListReducer(
  "packTypes",
  types.INVOICE_PACK_TYPE_LIST_REQUEST,
  types.INVOICE_PACK_TYPE_LIST_SUCCESS,
  types.INVOICE_PACK_TYPE_LIST_FAIL
);

// ============= ORDER TYPE & FROM REDUCERS =============

/**
 * Order types list reducer
 */
export const invoiceOrderTypeListReducer = createListReducer(
  "orderTypes",
  types.ORDER_TYPE_LIST_REQUEST,
  types.ORDER_TYPE_LIST_SUCCESS,
  types.ORDER_TYPE_LIST_FAIL
);

/**
 * Order from list reducer
 */
export const invoiceOrderFromListReducer = createListReducer(
  "orderFroms",
  types.ORDER_FROM_LIST_REQUEST,
  types.ORDER_FROM_LIST_SUCCESS,
  types.ORDER_FROM_LIST_FAIL
);

/**
 * Pending status list reducer
 */
export const pendingListReducer = createListReducer(
  "pendings",
  types.PENDING_LIST_REQUEST,
  types.PENDING_LIST_SUCCESS,
  types.PENDING_LIST_FAIL
);

// ============= SALES ORDER LIST REDUCERS =============

/**
 * Main sales order list reducer
 */
export const listSalesOrderInternalReducer = createListReducer(
  "salesorderinternal",
  types.SALESORDERINTERNAL_LIST_REQUEST,
  types.SALESORDERINTERNAL_LIST_SUCCESS,
  types.SALESORDERINTERNAL_LIST_FAIL
);

/**
 * Filtered sales order list reducer
 */
export const listSalesOrderInternalFilterReducer = createListReducer(
  "salesorderinternal",
  types.SALESORDERINTERNAL_FILTER_LIST_REQUEST,
  types.SALESORDERINTERNAL_FILTER_LIST_SUCCESS,
  types.SALESORDERINTERNAL_FILTER_LIST_FAIL,
  types.SALESORDERINTERNAL_FILTER_LIST_RESET
);

/**
 * Pending price sales order list reducer
 */
export const listSalesOrderInternalPendingPriceReducer = createListReducer(
  "salesorderinternalpendingprice",
  types.SALESORDERINTERNAL_PENDING_PRICE_LIST_REQUEST,
  types.SALESORDERINTERNAL_PENDING_PRICE_LIST_SUCCESS,
  types.SALESORDERINTERNAL_PENDING_PRICE_LIST_FAIL,
   types.PRICESUMMARY_CUSTOMER_PRODUCT_LIST_RESET
);

// ============= SALES ORDER DETAILS REDUCERS =============

/**
 * Sales order details reducer
 */
export const salesOrderInternalDetailsReducer = createDetailsReducer(
  "salesorderinternalsingle",
  types.SALESORDERINTERNAL_DETAILS_REQUEST,
  types.SALESORDERINTERNAL_DETAILS_SUCCESS,
  types.SALESORDERINTERNAL_DETAILS_FAIL,
  types.SALESORDERINTERNAL_DETAILS_RESET
);

/**
 * Packaging done details reducer
 */
export const salesOrderInternalPackageDoneDetailsReducer = createDetailsReducer(
  "salesorderinternalpackagingdonesingle",
  types.SALESORDERINTERNAL_PACKAGE_DONE_DETAILS_REQUEST,
  types.SALESORDERINTERNAL_PACKAGE_DONE_DETAILS_SUCCESS,
  types.SALESORDERINTERNAL_PACKAGE_DONE_DETAILS_FAIL,
  types.SALESORDERINTERNAL_PACKAGE_DONE_DETAILS_RESET
);

/**
 * Batch and mapped items details reducer
 */
export const listBatchAndMappedItemBasedOnProductReducer = createDetailsReducer(
  "batchAndMappedItems",
  types.BATCHANDMAPPEDITEMBASEDONPRODUCTID_DETAILS_REQUEST,
  types.BATCHANDMAPPEDITEMBASEDONPRODUCTID_DETAILS_SUCCESS,
  types.BATCHANDMAPPEDITEMBASEDONPRODUCTID_DETAILS_FAIL,
  types.BATCHANDMAPPEDITEMBASEDONPRODUCTID_DETAILS_RESET
);

/**
 * Latest batch and map details reducer
 */
export const latestBatchAndMapDetailsReducer = createDetailsReducer(
  "batchMapsingle",
  types.LATEST_BATCH_MAP_DETAILS_REQUEST,
  types.LATEST_BATCH_MAP_DETAILS_SUCCESS,
  types.LATEST_BATCH_MAP_DETAILS_FAIL,
  types.LATEST_BATCH_MAP_DETAILS_RESET
);

// ============= SALES ORDER CRUD REDUCERS =============

/**
 * Create sales order reducer
 */
export const SalesOrderInternalCreateReducer = createCrudReducer(
  types.SALESORDERINTERNAL_CREATE_REQUEST,
  types.SALESORDERINTERNAL_CREATE_SUCCESS,
  types.SALESORDERINTERNAL_CREATE_FAIL,
  types.SALESORDERINTERNAL_CREATE_RESET,
  "createdSalesOrder"
);

/**
 * Update sales order reducer
 */
export const SalesOrderInternalUpdateReducer = createCrudReducer(
  types.SALESORDERINTERNAL_UPDATE_REQUEST,
  types.SALESORDERINTERNAL_UPDATE_SUCCESS,
  types.SALESORDERINTERNAL_UPDATE_FAIL,
  types.SALESORDERINTERNAL_UPDATE_RESET,
  "updatedSalesOrder"
);

/**
 * Escalate sales order reducer
 */
export const SalesOrderInternalEscalateReducer = createCrudReducer(
  types.SALESORDERINTERNAL_ESCALATION_REQUEST,
  types.SALESORDERINTERNAL_ESCALATION_SUCCESS,
  types.SALESORDERINTERNAL_ESCALATION_FAIL,
  types.SALESORDERINTERNAL_ESCALATION_RESET,
  "escalatedSalesOrder"
);

// ============= PACKAGING REDUCERS =============

/**
 * Package sales order reducer
 */
export const SalesOrderInternalPackageReducer = createCrudReducer(
  types.SALESORDERINTERNAL_PACKAGE_REQUEST,
  types.SALESORDERINTERNAL_PACKAGE_SUCCESS,
  types.SALESORDERINTERNAL_PACKAGE_FAIL,
  types.SALESORDERINTERNAL_PACKAGE_RESET,
  "packagedSalesOrder"
);

/**
 * Package done reducer
 */
export const SalesOrderInternalPackageDoneReducer = createCrudReducer(
  types.SALESORDERINTERNAL_PACKAGE_DONE_REQUEST,
  types.SALESORDERINTERNAL_PACKAGE_DONE_SUCCESS,
  types.SALESORDERINTERNAL_PACKAGE_DONE_FAIL,
  types.SALESORDERINTERNAL_PACKAGE_DONE_RESET,
  "packageDoneSalesOrder"
);

/**
 * Package done update reducer
 */
export const SalesOrderInternalPackageDoneUpdateReducer = createCrudReducer(
  types.SALESORDERINTERNAL_PACKAGE_DONE_EDIT_REQUEST,
  types.SALESORDERINTERNAL_PACKAGE_DONE_EDIT_SUCCESS,
  types.SALESORDERINTERNAL_PACKAGE_DONE_EDIT_FAIL,
  types.SALESORDERINTERNAL_PACKAGE_DONE_EDIT_RESET,
  "updatedSalesOrderPackageDone"
);

// ============= PACKING STICKER REDUCERS =============

/**
 * List orders for packing sticker reducer
 */
export const listOrderPackingAndEditReducer = createListReducer(
  "orderList",
  types.LIST_ORDER_FOR_PACKING_EIDT_STICKER_REQUEST,
  types.LIST_ORDER_FOR_PACKING_EIDT_STICKER_SUCCESS,
  types.LIST_ORDER_FOR_PACKING_EIDT_STICKER_FAIL
);

/**
 * Packing sticker details reducer
 */
export const packingDetailsEditReducer = createDetailsReducer(
  "salesorderinternalpackagingdonesingle",
  types.DETAILS_EDIT_PACKING_STICKER_REQUEST,
  types.DETAILS_EDIT_PACKING_STICKER_SUCCESS,
  types.DETAILS_EDIT_PACKING_STICKER_FAIL,
  types.DETAILS_EDIT_PACKING_STICKER_RESET
);

/**
 * Create packing sticker reducer
 */
export const packingStickerCreateReducer = createCrudReducer(
  types.CREATE_PACKING_STICKER_REQUEST,
  types.CREATE_PACKING_STICKER_SUCCESS,
  types.CREATE_PACKING_STICKER_FAIL,
  types.CREATE_PACKING_STICKER_RESET,
  "createSticker"
);

/**
 * Update packing sticker reducer
 */
export const packingStickerUpdateReducer = createCrudReducer(
  types.UPDATE_PACKING_STICKER_REQUEST,
  types.UPDATE_PACKING_STICKER_SUCCESS,
  types.UPDATE_PACKING_STICKER_FAIL,
  types.UPDATE_PACKING_STICKER_RESET
);

// ============= MISCELLANEOUS REDUCERS =============

/**
 * Tally sales create reducer
 */
export const TallySalesCreateReducer = createCrudReducer(
  types.TALLYSALES_CREATE_REQUEST,
  types.TALLYSALES_CREATE_SUCCESS,
  types.TALLYSALES_CREATE_FAIL,
  types.TALLYSALES_CREATE_RESET,
  "tallySale"
);

/**
 * Price summary list reducer
 */
export const listPriceSummaryBasedOnProductAndCustomerReducer = createListReducer(
  "priceSummaryList",
  types.PRICESUMMARY_CUSTOMER_PRODUCT_LIST_REQUEST,
  types.PRICESUMMARY_CUSTOMER_PRODUCT_LIST_SUCCESS,
  types.PRICESUMMARY_CUSTOMER_PRODUCT_LIST_FAIL
);

/**
 * Job search single reducer
 */
export const ListJobSearchSingleReducer = (state = { singlejobsearch: {} }, action) => {
  switch (action.type) {
    case types.JOB_LIST_SINGLE_REQUEST:
      return { loading: true, error: null };
    case types.JOB_LIST_SINGLE_SUCCESS:
      return { loading: false, singlejobsearch: action.payload, error: null };
    case types.JOB_LIST_SINGLE_FAIL:
      return { loading: false, error: action.payload };
    case types.JOB_LIST_SINGLE_RESET:
      return { singlejobsearch: {} };
    default:
      return state;
  }
};

/**
 * Stockout pending update reducer
 */
export const StockoutPendingUpdateReducer = createCrudReducer(
  types.STOCKOUT_PENDING_UPDATE_REQUEST,
  types.STOCKOUT_PENDING_UPDATE_SUCCESS,
  types.STOCKOUT_PENDING_UPDATE_FAIL,
  types.STOCKOUT_PENDING_UPDATE_RESET,
  "updatedStockoutPending"
);